import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Modal } from 'foundations-components/transitional/components/Modal';
import _get from 'hs-lodash/get';
import { useContext, useEffect, useRef, useState } from 'react';
import { LINKED_PROPERTIES_CONFIG } from '../../constants/associations';
import { singularTypeToPlural } from '../../constants/types';
import { AppContext } from '../../contexts/app/context';
import { PreviewModalPropsContext } from '../../contexts/previewModal/PropsContext';
import { PreviewModalContext } from '../../contexts/previewModal/context';
import { PropertiesContext } from '../../contexts/properties/context';
import FormInputs from '../common/FormInputs/FormInputs';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function LinkedPropertiesModal({
  isOpen,
  closeModal
}) {
  const {
    crmObjectType,
    openUrl,
    portalId,
    apiWrapper,
    usageTracker,
    updateCrmObjectCallback,
    prosemirrorTrackerProperties,
    hubspotUserId
  } = useContext(PreviewModalPropsContext);
  const {
    objectEntity,
    previewModalActions
  } = useContext(PreviewModalContext);
  const {
    searchProperties
  } = useContext(PropertiesContext);
  const {
    appName
  } = useContext(AppContext);
  const linkedConfigRef = useRef(LINKED_PROPERTIES_CONFIG[singularTypeToPlural[crmObjectType]]);
  const [propsToRender, setPropsToRender] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const applyHandler = () => {
    const data = {};
    formFields.forEach(({
      name,
      value
    }) => {
      data[name] = value;
    });
    setLoading(true);
    previewModalActions.updateAssociatedObject({
      objectId: objectEntity.id,
      objectType: crmObjectType,
      data,
      cb: closeModal,
      isDate: false,
      usageTracker,
      updateCb: updateCrmObjectCallback
    });
  };
  const onValueChange = ({
    key,
    value,
    required
  }) => {
    const fieldToUpdateIndex = formFields.findIndex(el => el.name === key);
    const updatedField = Object.assign({}, formFields[fieldToUpdateIndex], {
      value,
      valid: true,
      required
    });
    formFields.splice(fieldToUpdateIndex, 1, updatedField);
    setFormFields(formFields.slice());
  };
  useEffect(() => {
    if (isOpen && !propsToRender.length) {
      const propsNamesToRender = Object.keys(linkedConfigRef.current);
      setPropsToRender(searchProperties[crmObjectType].filter(property => propsNamesToRender.includes(property.name)).sort((a, b) => {
        if (typeof b.displayOrder === 'number' && typeof a.displayOrder === 'number') {
          return b.displayOrder - a.displayOrder;
        }
        return 0;
      }));
    }
  }, [crmObjectType, searchProperties, isOpen, objectEntity, propsToRender.length]);
  useEffect(() => {
    if (isOpen && !formFields.length) {
      setFormFields(propsToRender.map(({
        name,
        required,
        fieldType
      }) => ({
        name,
        required: required || false,
        valid: true,
        value: objectEntity.properties[name] || '',
        fieldType: fieldType,
        disabled: false,
        tooltip: ''
      })));
    }
  }, [formFields.length, isOpen, objectEntity, propsToRender]);
  useEffect(() => {
    if (!isOpen) setLoading(false);
  }, [isOpen]);
  if (!isOpen || !formFields.length) return null;
  return /*#__PURE__*/_jsxs(Modal, {
    use: "default",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      paddingBlock: "0",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h4",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "associationModals.linkedProperties.title"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: closeModal
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsx(_Fragment, {
        children: propsToRender.map(property => {
          const currentField = formFields.find(el => el.name === property.name);
          const correlatedField = currentField ? LINKED_PROPERTIES_CONFIG[singularTypeToPlural[crmObjectType]][currentField.name] : undefined;
          const correlatedFieldEntity = correlatedField ? formFields.find(el => el.name === correlatedField.relatedProperty) : '';
          const isError = !_get(currentField, 'valid');
          const fieldvalue = _get(currentField, 'value', '');
          const disabled = _get(currentField, 'disabled', false);
          const tooltip = _get(currentField, 'tooltip', '');
          return /*#__PURE__*/_jsx(FormControl, {
            required: currentField ? currentField.required : property.required,
            label: property.label,
            error: isError,
            validationMessage: isError,
            tooltip: tooltip,
            children: /*#__PURE__*/_jsx(FormInputs, {
              property: property,
              currentValue: fieldvalue,
              onFormValueChanged: onValueChange,
              disabled: disabled,
              objectType: singularTypeToPlural[crmObjectType],
              portalId: portalId,
              openUrl: openUrl,
              snippetsApiWrapper: apiWrapper.snippets,
              correlatedField: correlatedField,
              correlatedFieldValue: correlatedFieldEntity ? correlatedFieldEntity.value : '',
              prosemirrorTrackerProperties: prosemirrorTrackerProperties,
              hubspotUserId: hubspotUserId
            })
          }, property.name);
        })
      })
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(LoadingButton, {
        use: "primary",
        onClick: applyHandler,
        loading: loading,
        "data-test-id": `${appName}-save-linked-properties`,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "associationModals.linkedProperties.apply"
        })
      }), /*#__PURE__*/_jsx(Button, {
        use: "secondary",
        onClick: closeModal,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "associationModals.linkedProperties.cancel"
        })
      })]
    })]
  });
}
export default LinkedPropertiesModal;